/** Visitors Desk analyst API (aamcirc/admin/routers/visitors_agent.py). 404 on every route means the agent is off. */
import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from './client'
import type { AgentGroup, Direction } from '../lib/visitorsAgent'

const P = '/api/visitors/desk/agent'

export interface Link { label: string; href: string }
export interface ExecLine { group: AgentGroup; direction: Direction; measure: string; line: string; href: string | null }
export interface DetailSection { group: AgentGroup; heading: string; body: string }
export interface AgentRun {
  id: number; scope: string; kind: string; period_start: string; period_end: string
  status: 'queued' | 'running' | 'done' | 'failed'
  executive: ExecLine[] | null; detail: DetailSection[] | null
  error: string | null; model: string | null; cost_cents: number | null
  created_at: string | null; started_at?: string | null; finished_at: string | null
}
export interface Rec {
  id: number; position: number; group: AgentGroup; goal: string; title: string; body: string
  evidence: Link[]; confidence: string | null
  handoff_kind: string | null; handoff: Record<string, unknown>
  handed_off_at: string | null; handoff_href?: string | null; handoff_available: boolean
}
export interface AgentRunFull extends AgentRun { recommendations: Rec[] }
export interface Conversation {
  id: number; scope: string; run_id: number | null; recommendation_id: number | null
  title: string; created_at: string | null; updated_at: string | null
}
export interface Message {
  id: number; conversation_id: number; role: 'user' | 'assistant'; body: string
  citations: Link[]; status: 'queued' | 'running' | 'done' | 'failed'; error: string | null
  model: string | null; created_at: string | null
}
export interface ConversationFull extends Conversation { messages: Message[] }

export const agentApi = {
  settings: () => api.get<{ analyst_on: boolean }>(`${P}/settings`),
  putSettings: (analyst_on: boolean) => api.put<{ analyst_on: boolean }>(`${P}/settings`, { analyst_on }),
  runs: (scope: string) => api.get<{ runs: AgentRun[] }>(`${P}/runs?scope=${scope}&limit=20`),
  latest: (scope: string) => api.get<{ run: AgentRun | null; active: AgentRun | null }>(`${P}/runs/latest?scope=${scope}`),
  run: (id: number) => api.get<AgentRunFull>(`${P}/runs/${id}`),
  queueRun: (scope: string) => api.post<AgentRun>(`${P}/runs`, { scope }),
  conversations: (scope: string) => api.get<{ conversations: Conversation[] }>(`${P}/conversations?scope=${scope}&limit=30`),
  createConversation: (b: { scope: string; run_id?: number; recommendation_id?: number }) => api.post<Conversation>(`${P}/conversations`, b),
  conversation: (id: number) => api.get<ConversationFull>(`${P}/conversations/${id}`),
  send: (id: number, body: string) => api.post<{ user: Message; assistant: Message }>(`${P}/conversations/${id}/messages`, { body }),
  handoff: (recId: number) => api.post<{ kind: string; href: string }>(`${P}/recommendations/${recId}/handoff`),
}

/** The agent's settings when it is on, null when it is off (404), and an error for anything else. */
export function useAgentSettings(enabled = true) {
  return useQuery({
    queryKey: ['vd-agent-settings'],
    queryFn: () => agentApi.settings().then(
      (s) => s,
      (err: unknown) => {
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return null
        throw err
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}
